import {
  ActionIcon,
  Box,
  Button,
  Container,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft,
  IconChevronRight,
  IconHeart,
  IconSend,
  IconUserMinus,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FriendLink } from "../../shared/types";
import {
  fetchDMConversation,
  sendDM,
  type DirectMessage,
  type FriendPresence,
  type Session,
} from "../lib/api";

interface Props {
  session: Session;
  acceptedFriends: FriendLink[];
  pendingFriends: FriendLink[];
  friendsOnline: FriendPresence[];
  unreadPerFriend: Record<string, number>;
  lastDmNotification: {
    fromAddress: string;
    fromName: string;
    text?: string;
    seq: number;
  } | null;
  onSendFriendRequest: (address: string) => Promise<void>;
  onAcceptFriend: (friend: FriendLink) => Promise<void>;
  onRemoveFriend: (friend: FriendLink) => Promise<void>;
  onVisitRoom: (roomId: string) => void;
  onUnreadChange: () => void;
}

export function SocialScreen({
  session,
  acceptedFriends,
  pendingFriends,
  friendsOnline,
  unreadPerFriend,
  lastDmNotification,
  onSendFriendRequest,
  onAcceptFriend,
  onRemoveFriend,
  onVisitRoom,
  onUnreadChange,
}: Props) {
  const [friendAddress, setFriendAddress] = useState("");
  const [activeChat, setActiveChat] = useState<{
    address: string;
    name: string;
  } | null>(null);
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [dmText, setDmText] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<FriendLink | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Presence map
  const presenceMap = new Map<string, FriendPresence>();
  for (const f of friendsOnline) {
    presenceMap.set(f.address, f);
  }

  // Sort friends: online first
  const sortedFriends = [...acceptedFriends].sort((a, b) => {
    const aAddr =
      a.userAddress === session.address ? a.friendAddress : a.userAddress;
    const bAddr =
      b.userAddress === session.address ? b.friendAddress : b.userAddress;
    const aOnline = presenceMap.has(aAddr) ? 0 : 1;
    const bOnline = presenceMap.has(bAddr) ? 0 : 1;
    return aOnline - bOnline;
  });

  // Open conversation
  const openConversation = useCallback(
    async (address: string, name: string) => {
      setActiveChat({ address, name });
      setLoadingMessages(true);
      setMessages([]);
      setDmText("");
      try {
        const msgs = await fetchDMConversation(session, address);
        setMessages(msgs);
        onUnreadChange();
      } catch {
        // ignore
      } finally {
        setLoadingMessages(false);
      }
    },
    [session, onUnreadChange]
  );

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      const viewport = scrollRef.current.querySelector(
        "[data-radix-scroll-area-viewport], .mantine-ScrollArea-viewport"
      );
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    }
  }, [messages]);

  // Instant append when a DM notification arrives for the active conversation
  useEffect(() => {
    if (
      !lastDmNotification?.text ||
      !activeChat ||
      lastDmNotification.fromAddress !== activeChat.address
    )
      return;
    setMessages((prev) => [
      ...prev,
      {
        id: `ws-${lastDmNotification.seq}`,
        senderAddress: lastDmNotification.fromAddress,
        text: lastDmNotification.text!,
        createdAt: new Date().toISOString(),
      },
    ]);
  }, [lastDmNotification?.seq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for new messages while conversation is open
  useEffect(() => {
    if (!activeChat) return;
    const timer = window.setInterval(async () => {
      try {
        const msgs = await fetchDMConversation(session, activeChat.address);
        setMessages(msgs);
        onUnreadChange();
      } catch {
        // ignore
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [activeChat, session, onUnreadChange]);

  const handleSend = async () => {
    if (!activeChat || !dmText.trim() || sending) return;
    const text = dmText.trim();
    setSending(true);
    setDmText("");
    try {
      await sendDM(session, activeChat.address, text);
      setMessages((prev) => [
        ...prev,
        {
          id: `temp-${Date.now()}`,
          senderAddress: session.address,
          text,
          createdAt: new Date().toISOString(),
        },
      ]);
    } catch {
      setDmText(text);
    } finally {
      setSending(false);
    }
  };

  const handleAddFriend = () => {
    const nextAddress = friendAddress.trim();
    if (!nextAddress) return;
    void (async () => {
      try {
        await onSendFriendRequest(nextAddress);
        setFriendAddress("");
        notifications.show({
          title: "Friend request sent",
          message: `Sent to ${nextAddress.slice(0, 8)}...`,
          color: "teal",
        });
      } catch (error) {
        notifications.show({
          title: "Could not send request",
          message:
            error instanceof Error
              ? error.message
              : "Please try again shortly.",
          color: "red",
        });
      }
    })();
  };

  // Conversation view
  if (activeChat) {
    return (
      <Container size="sm" py="xl">
        <Paper
          radius="md"
          withBorder
          bg="white"
          style={{ display: "flex", flexDirection: "column", height: 560 }}
        >
          {/* Header */}
          <Group
            px="md"
            py="sm"
            gap={8}
            wrap="nowrap"
            style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}
          >
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setActiveChat(null)}
            >
              <IconArrowLeft size={18} />
            </ActionIcon>
            <Text fw={700} size="sm" c="dark" truncate style={{ flex: 1 }}>
              {activeChat.name}
            </Text>
          </Group>

          {/* Messages */}
          <ScrollArea style={{ flex: 1 }} px="md" py="xs" ref={scrollRef}>
            {loadingMessages && (
              <Text size="xs" c="dimmed" ta="center" py="xl">
                Loading...
              </Text>
            )}
            {!loadingMessages && messages.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" py="xl">
                No messages yet. Say hi!
              </Text>
            )}
            <Stack gap={4}>
              {messages.map((msg) => {
                const isMine = msg.senderAddress === session.address;
                return (
                  <Box
                    key={msg.id}
                    style={{
                      display: "flex",
                      justifyContent: isMine ? "flex-end" : "flex-start",
                    }}
                  >
                    <Box
                      px="sm"
                      py={6}
                      style={{
                        maxWidth: "75%",
                        borderRadius: 12,
                        background: isMine
                          ? "rgba(255, 146, 43, 0.12)"
                          : "rgba(0,0,0,0.04)",
                      }}
                    >
                      <Text
                        size="sm"
                        c="dark"
                        style={{ wordBreak: "break-word" }}
                      >
                        {msg.text}
                      </Text>
                      <Text
                        size="xs"
                        c="gray.5"
                        ta={isMine ? "right" : "left"}
                        mt={2}
                      >
                        {new Date(msg.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Text>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </ScrollArea>

          {/* Input */}
          <Group
            px="sm"
            py="sm"
            gap={6}
            wrap="nowrap"
            style={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}
          >
            <TextInput
              placeholder="Type a message..."
              size="sm"
              value={dmText}
              onChange={(e) => setDmText(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              style={{ flex: 1 }}
            />
            <ActionIcon
              variant="filled"
              color="orange"
              size="lg"
              disabled={!dmText.trim() || sending}
              onClick={() => void handleSend()}
            >
              <IconSend size={16} />
            </ActionIcon>
          </Group>
        </Paper>
      </Container>
    );
  }

  const confirmRemoveName = confirmRemove
    ? confirmRemove.displayName ||
      `${(confirmRemove.userAddress === session.address
        ? confirmRemove.friendAddress
        : confirmRemove.userAddress
      ).slice(0, 8)}...`
    : "";

  // Friends list view
  return (
    <Container size="sm" py="xl">
      <Modal
        opened={confirmRemove !== null}
        onClose={() => setConfirmRemove(null)}
        title="Remove Friend"
        centered
        size="xs"
      >
        <Text size="sm" c="dark" mb="md">
          Are you sure you want to remove <b>{confirmRemoveName}</b> as a
          friend?
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button
            size="xs"
            variant="default"
            onClick={() => setConfirmRemove(null)}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            color="red"
            leftSection={<IconUserMinus size={14} />}
            onClick={() => {
              if (confirmRemove) {
                void onRemoveFriend(confirmRemove);
              }
              setConfirmRemove(null);
            }}
          >
            Remove
          </Button>
        </Group>
      </Modal>

      <Stack gap="md">
        {/* Add friend */}
        <Paper p="md" radius="md" withBorder bg="white">
          <Group gap={8} mb="sm">
            <IconUserPlus size={18} color="#ff922b" />
            <Text fw={700} size="sm" c="dark">
              Add Friend
            </Text>
          </Group>
          <Group gap="xs">
            <TextInput
              placeholder="Wallet address (tz1...)"
              value={friendAddress}
              onChange={(e) => setFriendAddress(e.currentTarget.value)}
              size="sm"
              style={{ flex: 1 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddFriend();
              }}
            />
            <ActionIcon
              variant="filled"
              size="lg"
              color="orange"
              onClick={handleAddFriend}
            >
              <IconUserPlus size={18} />
            </ActionIcon>
          </Group>
        </Paper>

        {/* Pending requests */}
        {pendingFriends.length > 0 && (
          <Paper p="md" radius="md" withBorder bg="white">
            <Group gap={8} mb="sm">
              <IconHeart size={18} color="#ff922b" />
              <Text fw={700} size="sm" c="dark">
                Friend Requests
              </Text>
              <Box
                px={8}
                py={2}
                style={{
                  borderRadius: 10,
                  background: "rgba(255, 146, 43, 0.12)",
                }}
              >
                <Text size="xs" fw={700} c="orange.7">
                  {pendingFriends.length}
                </Text>
              </Box>
            </Group>
            <Stack gap={4}>
              {pendingFriends.map((f) => {
                const other =
                  f.userAddress === session.address
                    ? f.friendAddress
                    : f.userAddress;
                return (
                  <Group
                    key={`${f.userAddress}:${f.friendAddress}`}
                    justify="space-between"
                    wrap="nowrap"
                    px={4}
                    py={4}
                  >
                    <Text size="sm" c="dark" truncate style={{ flex: 1 }}>
                      {f.displayName || `${other.slice(0, 8)}...`}
                    </Text>
                    <Button
                      size="compact-xs"
                      variant="light"
                      color="teal"
                      onClick={() => void onAcceptFriend(f)}
                    >
                      Accept
                    </Button>
                  </Group>
                );
              })}
            </Stack>
          </Paper>
        )}

        {/* Friends list */}
        <Paper p="md" radius="md" withBorder bg="white">
          <Group gap={8} mb="sm">
            <IconUsers size={18} color="#ff922b" />
            <Text fw={700} size="sm" c="dark">
              Friends
            </Text>
            {acceptedFriends.length > 0 && (
              <Text size="xs" c="gray.5">
                ({acceptedFriends.length})
              </Text>
            )}
          </Group>

          {sortedFriends.length === 0 && (
            <Text size="sm" c="dimmed">
              No friends yet. Add someone above!
            </Text>
          )}

          <Stack gap={0}>
            {sortedFriends.map((f) => {
              const otherAddress =
                f.userAddress === session.address
                  ? f.friendAddress
                  : f.userAddress;
              const name = f.displayName || `${otherAddress.slice(0, 8)}...`;
              const presence = presenceMap.get(otherAddress);
              const isOnline = Boolean(presence);
              const unread = unreadPerFriend[otherAddress] ?? 0;

              return (
                <Box
                  key={`${f.userAddress}:${f.friendAddress}`}
                  px={8}
                  py={8}
                  style={{
                    borderBottom: "1px solid rgba(0,0,0,0.05)",
                    cursor: "pointer",
                    borderRadius: 8,
                    transition: "background 0.12s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "rgba(0,0,0,0.02)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "";
                  }}
                  onClick={() => void openConversation(otherAddress, name)}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Group gap={6} wrap="nowrap">
                        <Box
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: isOnline ? "#40c057" : "#adb5bd",
                            flexShrink: 0,
                          }}
                        />
                        <Text size="sm" fw={600} c="dark" truncate>
                          {name}
                        </Text>
                        {unread > 0 && (
                          <Box
                            style={{
                              minWidth: 18,
                              height: 18,
                              borderRadius: 9,
                              background: "#ff0000",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 10,
                              color: "white",
                              fontWeight: 700,
                              lineHeight: 1,
                              padding: "0 5px",
                              flexShrink: 0,
                            }}
                          >
                            {unread > 99 ? "99+" : unread}
                          </Box>
                        )}
                      </Group>
                      <Text size="xs" c="gray.5" truncate ml={14}>
                        {presence
                          ? presence.roomOwnerName
                            ? `in ${presence.roomOwnerName}'s room`
                            : `in ${presence.roomName}`
                          : "Offline"}
                      </Text>
                    </Box>
                    <Group gap={4} wrap="nowrap">
                      {presence && (
                        <Button
                          size="compact-xs"
                          variant="light"
                          color="orange"
                          onClick={(e) => {
                            e.stopPropagation();
                            onVisitRoom(presence.roomId);
                          }}
                        >
                          Visit
                        </Button>
                      )}
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmRemove(f);
                        }}
                      >
                        <IconUserMinus size={14} />
                      </ActionIcon>
                      <IconChevronRight size={14} color="rgba(0,0,0,0.2)" />
                    </Group>
                  </Group>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
